import { useState, useEffect, useMemo } from 'react';

// API function to fetch pickup fees
const fetchPickupFees = async (brandId) => {
  const response = await fetch(`/api/v5/products/pickup_fee?brand_id=${brandId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data;
};

// Main hook
export const usePickupFees = (brandId, options = {}) => {
  const { enabled = true } = options;
  
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !brandId) return;

    const loadPickupFees = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const result = await fetchPickupFees(brandId);
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadPickupFees();
  }, [brandId, enabled]);

  // Memoized processed pickup fees
  const pickupFees = useMemo(() => {
    return data?.pickup_fees || [];
  }, [data?.pickup_fees]);

  // Helper functions for common operations
  const getDefaultPickupFee = useMemo(() => {
    return pickupFees.find(fee => fee.default === true) || null;
  }, [pickupFees]);

  const getWaivedPickupFees = useMemo(() => {
    return pickupFees.filter(fee => fee.waived === true);
  }, [pickupFees]);

  const getPaidPickupFees = useMemo(() => {
    return pickupFees.filter(fee => fee.waived === false);
  }, [pickupFees]);

  const getHourlyPickupFees = useMemo(() => {
    return pickupFees.filter(fee => fee.hourly === true);
  }, [pickupFees]);

  const getPickupFeeById = (feeId) => {
    return pickupFees.find(fee => fee.id === feeId) || null;
  };

  const getPickupFeesBySku = (sku) => {
    return pickupFees.filter(fee => fee.sku === sku);
  };

  const getPickupFeesByTimeSlot = (hour) => {
    return pickupFees.filter(fee => 
      fee.hourly && 
      fee.ready_time_hour <= hour && 
      fee.close_time_hour > hour
    );
  };

  const formatPrice = (priceCents) => {
    return `$${(priceCents / 100).toFixed(2)}`;
  };

  // Group fees by category for easy rendering
  const groupedFees = useMemo(() => {
    const waived = getWaivedPickupFees;
    const paid = getPaidPickupFees;
    const hourly = getHourlyPickupFees;
    const standard = pickupFees.filter(fee => !fee.hourly);

    return {
      waived,
      paid,
      hourly,
      standard,
      all: pickupFees
    };
  }, [pickupFees, getWaivedPickupFees, getPaidPickupFees, getHourlyPickupFees]);

  // Manual refetch function
  const refetch = async () => {
    if (!brandId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await fetchPickupFees(brandId);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // Data
    pickupFees,
    groupedFees,
    defaultPickupFee: getDefaultPickupFee,
    
    // Loading states
    isLoading,
    error,
    isError: !!error,
    
    // Actions
    refetch,
    
    // Helper functions
    getPickupFeeById,
    getPickupFeesBySku,
    getPickupFeesByTimeSlot,
    getWaivedPickupFees,
    getPaidPickupFees,
    getHourlyPickupFees,
    formatPrice,
    
    // Computed states
    isEmpty: pickupFees.length === 0,
    hasPickupFees: pickupFees.length > 0,
    pickupFeeCount: pickupFees.length,
    
    // Raw response for debugging
    rawResponse: data,
  };
};
